import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  Switch,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp } from '@react-navigation/native';
import biometricService from '../../services/biometricService';

// Import icons
import backIcon from '../../assets/icons/back.png';
import faceIdIcon from '../../assets/icons/faceIdIcon.png';

import { createLogger } from '../../utils/logger';
const logger = createLogger('BiometricSettings');

interface BiometricSettingsScreenProps {
  navigation: NavigationProp<any>;
}

const BiometricSettingsScreen: React.FC<BiometricSettingsScreenProps> = ({ navigation }) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [biometricType, setBiometricType] = useState<string | null>(null);
  const [isBiometricAvailable, setIsBiometricAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initBiometricSettings();
  }, []);

  const initBiometricSettings = async () => {
    setIsLoading(true);
    try {
      // Check if biometrics are available
      const capabilities = await biometricService.isBiometricAvailable();
      setIsBiometricAvailable(capabilities.available);

      if (capabilities.available) {
        // Get biometric type
        const type = await biometricService.getBiometricType();
        setBiometricType(type);

        // Check if biometric login is enabled
        const enabled = await biometricService.isBiometricLoginEnabled();
        setIsEnabled(enabled);
      }
    } catch (error) {
      logger.error('Error initializing biometric settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleBiometric = async (value: boolean) => {
    if (value) {
      // Enabling biometric login
      Alert.alert(
        'Enable Biometric Login',
        `To enable ${biometricType || 'biometric'} login, you'll need to verify your identity on the next login.`,
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'OK',
            onPress: async () => {
              try {
                // Check if biometric authentication works
                const authResult = await biometricService.authenticateWithBiometrics(
                  'Verify your identity to enable biometric login'
                );

                if (authResult.success) {
                  setIsEnabled(true);
                  Alert.alert(
                    'Success',
                    `${biometricType || 'Biometric'} login will be enabled on your next successful login.`
                  );
                } else {
                  Alert.alert(
                    'Authentication Failed',
                    authResult.error || 'Unable to authenticate with biometrics'
                  );
                }
              } catch (error) {
                logger.error('Error enabling biometric login:', error);
                Alert.alert('Error', 'Failed to enable biometric login');
              }
            },
          },
        ]
      );
    } else {
      // Disabling biometric login
      Alert.alert(
        'Disable Biometric Login',
        `Are you sure you want to disable ${biometricType || 'biometric'} login? You'll need to enter your email and password to log in.`,
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Disable',
            style: 'destructive',
            onPress: async () => {
              try {
                const result = await biometricService.disableBiometricLogin();
                if (result.success) {
                  setIsEnabled(false);
                  Alert.alert('Success', 'Biometric login has been disabled');
                } else {
                  Alert.alert('Error', result.error || 'Failed to disable biometric login');
                }
              } catch (error) {
                logger.error('Error disabling biometric login:', error);
                Alert.alert('Error', 'Failed to disable biometric login');
              }
            },
          },
        ]
      );
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Image source={backIcon} style={styles.headerIcon} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Biometric Settings</Text>
          <View style={styles.placeholder} />
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.headerIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Biometric Settings</Text>
        <View style={styles.placeholder} />
      </View>

      <View style={styles.content}>
        {!isBiometricAvailable ? (
          <View style={styles.unavailableContainer}>
            <Image source={faceIdIcon} style={styles.unavailableIcon} />
            <Text style={styles.unavailableTitle}>Biometric Authentication Not Available</Text>
            <Text style={styles.unavailableText}>
              Your device doesn't support biometric authentication, or it hasn't been set up yet.
              Please check your device settings.
            </Text>
          </View>
        ) : (
          <>
            <View style={styles.biometricCard}>
              <View style={styles.biometricInfo}>
                <Image source={faceIdIcon} style={styles.biometricIcon} />
                <View style={styles.biometricTextContainer}>
                  <Text style={styles.biometricTitle}>
                    {biometricType || 'Biometric'} Login
                  </Text>
                  <Text style={styles.biometricSubtitle}>
                    {isEnabled
                      ? `${biometricType || 'Biometric'} login is currently enabled`
                      : `Use ${biometricType || 'biometric'} authentication to log in quickly and securely`}
                  </Text>
                </View>
              </View>
              <Switch
                trackColor={{ false: '#767577', true: '#29BFC0' }}
                thumbColor={isEnabled ? '#fff' : '#f4f3f4'}
                ios_backgroundColor="#767577"
                onValueChange={handleToggleBiometric}
                value={isEnabled}
              />
            </View>

            <View style={styles.infoSection}>
              <Text style={styles.infoTitle}>How It Works</Text>
              <Text style={styles.infoText}>
                • When enabled, you can log in using {biometricType || 'biometric authentication'} instead of your password
              </Text>
              <Text style={styles.infoText}>
                • Your credentials are stored securely on your device
              </Text>
              <Text style={styles.infoText}>
                • You can disable this feature at any time
              </Text>
            </View>

            <View style={styles.securitySection}>
              <Text style={styles.securityTitle}>Security Note</Text>
              <Text style={styles.securityText}>
                Biometric login uses your device's secure enclave to store your credentials.
                Your biometric data never leaves your device and is never sent to our servers.
              </Text>
            </View>
          </>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(15),
    marginTop: ms(10),
    backgroundColor: '#F3F6F8',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  placeholder: {
    width: s(24),
  },
  content: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  unavailableContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: ms(80),
    paddingHorizontal: ms(40),
  },
  unavailableIcon: {
    width: s(80),
    height: s(80),
    opacity: 0.3,
    marginBottom: ms(20),
  },
  unavailableTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginBottom: ms(10),
  },
  unavailableText: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(24, 0.3),
  },
  biometricCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  biometricInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  biometricIcon: {
    width: s(50),
    height: s(50),
    marginRight: ms(15),
  },
  biometricTextContainer: {
    flex: 1,
    marginRight: ms(10),
  },
  biometricTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(5),
  },
  biometricSubtitle: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(20, 0.3),
  },
  infoSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  infoTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(15),
  },
  infoText: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(24, 0.3),
    marginBottom: ms(8),
  },
  securitySection: {
    backgroundColor: '#E8F5F5',
    borderRadius: ms(15),
    padding: ms(20),
    borderWidth: ms(1),
    borderColor: '#29BFC0',
  },
  securityTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#29BFC0',
    marginBottom: ms(10),
  },
  securityText: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(22, 0.3),
  },
});

export default BiometricSettingsScreen;
